import React, { useCallback, useEffect, useState } from 'react';
import {
  FaShieldAlt,
  FaUser,
  FaIdCard,
  FaBell,
} from 'react-icons/fa';

export type LinkedServiceId = 'yandex' | 'github' | 'telegram' | 'vk' | 'max';

export type LinkedServiceState = {
  connected: boolean;
  accountLabel: string;
  needsReconnect?: boolean;
};

export type LinkedAccountsPrefs = {
  services: Record<LinkedServiceId, LinkedServiceState>;
  loginVia: Record<LinkedServiceId, boolean>;
  syncAvatar: boolean;
  syncUsername: boolean;
  syncNotifications: boolean;
};

const STORAGE_PREFIX = 'sciplex_linked_accounts_';

export const DEFAULT_LINKED_ACCOUNTS: LinkedAccountsPrefs = {
  services: {
    yandex: { connected: false, accountLabel: '' },
    github: { connected: false, accountLabel: '' },
    telegram: { connected: false, accountLabel: '' },
    vk: { connected: false, accountLabel: '' },
    max: { connected: false, accountLabel: '' },
  },
  loginVia: {
    yandex: false,
    github: false,
    telegram: false,
    vk: false,
    max: false,
  },
  syncAvatar: true,
  syncUsername: true,
  syncNotifications: true,
};

export function loadLinkedAccountsPrefs(userId: number): LinkedAccountsPrefs {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    if (!raw) {
      return JSON.parse(JSON.stringify(DEFAULT_LINKED_ACCOUNTS)) as LinkedAccountsPrefs;
    }
    const parsed = JSON.parse(raw) as Partial<LinkedAccountsPrefs>;
    return {
      services: { ...DEFAULT_LINKED_ACCOUNTS.services, ...(parsed.services || {}) },
      loginVia: { ...DEFAULT_LINKED_ACCOUNTS.loginVia, ...(parsed.loginVia || {}) },
      syncAvatar: parsed.syncAvatar !== false,
      syncUsername: parsed.syncUsername !== false,
      syncNotifications: parsed.syncNotifications !== false,
    };
  } catch {
    return JSON.parse(JSON.stringify(DEFAULT_LINKED_ACCOUNTS)) as LinkedAccountsPrefs;
  }
}

export function saveLinkedAccountsPrefs(userId: number, prefs: LinkedAccountsPrefs) {
  localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify(prefs));
}

function ToggleSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      className={`settings-notif-toggle${checked ? ' is-on' : ''}`}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
    >
      <span className="settings-notif-toggle__knob" />
    </button>
  );
}

function ServiceIcon({ id }: { id: LinkedServiceId }) {
  return (
    <img
      src={`/linked/${id}.png`}
      alt=""
      className="settings-linked-service-logo"
    />
  );
}

const SERVICE_META: Record<
  LinkedServiceId,
  { nameKey: string; descKey: string; icon: React.ReactNode; demoAccount: string }
> = {
  yandex: {
    nameKey: 'settings.linkedYandex',
    descKey: 'settings.linkedYandexDesc',
    icon: <ServiceIcon id="yandex" />,
    demoAccount: 'cookie@yandex.ru',
  },
  github: {
    nameKey: 'settings.linkedGithub',
    descKey: 'settings.linkedGithubDesc',
    icon: <ServiceIcon id="github" />,
    demoAccount: 'cookie-dev',
  },
  telegram: {
    nameKey: 'settings.linkedTelegram',
    descKey: 'settings.linkedTelegramDesc',
    icon: <ServiceIcon id="telegram" />,
    demoAccount: '@cookie_dev',
  },
  vk: {
    nameKey: 'settings.linkedVk',
    descKey: 'settings.linkedVkDesc',
    icon: <ServiceIcon id="vk" />,
    demoAccount: 'id123456789',
  },
  max: {
    nameKey: 'settings.linkedMax',
    descKey: 'settings.linkedMaxDesc',
    icon: <ServiceIcon id="max" />,
    demoAccount: 'cookie_max',
  },
};

const SERVICE_ORDER: LinkedServiceId[] = ['yandex', 'github', 'telegram', 'vk', 'max'];

type Props = {
  userId: number;
  t: (key: string) => string;
  onCancel: () => void;
};

export function LinkedAccountsSettings({ userId, t, onCancel }: Props) {
  const [prefs, setPrefs] = useState<LinkedAccountsPrefs>(() => loadLinkedAccountsPrefs(userId));
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setPrefs(loadLinkedAccountsPrefs(userId));
  }, [userId]);

  const handleSave = useCallback(() => {
    saveLinkedAccountsPrefs(userId, prefs);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  }, [userId, prefs]);

  const connect = (id: LinkedServiceId) => {
    setPrefs((p) => ({
      ...p,
      services: {
        ...p.services,
        [id]: {
          connected: true,
          accountLabel: SERVICE_META[id].demoAccount,
          needsReconnect: false,
        },
      },
      loginVia: { ...p.loginVia, [id]: true },
    }));
  };

  const disconnect = (id: LinkedServiceId) => {
    setPrefs((p) => ({
      ...p,
      services: {
        ...p.services,
        [id]: { connected: false, accountLabel: '', needsReconnect: false },
      },
      loginVia: { ...p.loginVia, [id]: false },
    }));
  };

  const reconnect = (id: LinkedServiceId) => {
    setPrefs((p) => ({
      ...p,
      services: {
        ...p.services,
        [id]: {
          connected: true,
          accountLabel: SERVICE_META[id].demoAccount,
          needsReconnect: false,
        },
      },
    }));
  };

  return (
    <div className="settings-container settings-linked">
      <h1 className="settings-title">{t('settings.sectionLinkedAccounts')}</h1>
      <p className="settings-subtitle">{t('settings.linkedIntro')}</p>
      {savedFlash && <div className="alert alert-success">{t('settings.linkedSaved')}</div>}

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.linkedExternalTitle')}</h2>
        <ul className="settings-linked-list">
          {SERVICE_ORDER.map((id) => {
            const meta = SERVICE_META[id];
            const state = prefs.services[id];
            const connected = state.connected;
            const statusText = connected
              ? (state.needsReconnect ? t('settings.linkedStatusReconnectHint') : t('settings.linkedStatusConnected'))
              : t('settings.linkedStatusDisconnected');
            const subtitle = connected
              ? (state.accountLabel || t(meta.descKey))
              : t(meta.descKey);

            return (
              <li key={id} className="settings-linked-row">
                <span className="settings-linked-row__icon" aria-hidden>
                  {meta.icon}
                </span>
                <div className="settings-linked-row__text">
                  <span className="settings-linked-row__name">{t(meta.nameKey)}</span>
                  <span className="settings-linked-row__sub">{subtitle}</span>
                </div>
                <span className={`settings-linked-status${connected ? (state.needsReconnect ? ' is-warn' : ' is-on') : ''}`}>
                  <span className="settings-linked-status__dot" aria-hidden />
                  {statusText}
                </span>
                {connected && state.needsReconnect ? (
                  <button type="button" className="settings-linked-btn settings-linked-btn--accent" onClick={() => reconnect(id)}>
                    {t('settings.linkedReconnect')}
                  </button>
                ) : connected ? (
                  <button type="button" className="settings-linked-btn settings-linked-btn--danger" onClick={() => disconnect(id)}>
                    {t('settings.linkedDisconnect')}
                  </button>
                ) : (
                  <button type="button" className="settings-linked-btn settings-linked-btn--accent" onClick={() => connect(id)}>
                    {t('settings.linkedConnect')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.linkedLoginTitle')}</h2>
        <p className="settings-appear-block__desc">{t('settings.linkedLoginDesc')}</p>
        <ul className="settings-linked-toggles">
          {SERVICE_ORDER.map((id) => (
            <li key={id} className="settings-notif-row settings-appear-effect-row">
              <span className="settings-linked-row__icon settings-linked-row__icon--sm" aria-hidden>
                {SERVICE_META[id].icon}
              </span>
              <div className="settings-notif-row__text">
                <span className="settings-notif-row__title">
                  {t('settings.linkedLoginVia').replace('{{service}}', t(SERVICE_META[id].nameKey))}
                </span>
              </div>
              <ToggleSwitch
                checked={prefs.loginVia[id]}
                onChange={(v) =>
                  setPrefs((p) => ({
                    ...p,
                    loginVia: { ...p.loginVia, [id]: v },
                    services: v && !p.services[id].connected
                      ? {
                          ...p.services,
                          [id]: {
                            connected: true,
                            accountLabel: SERVICE_META[id].demoAccount,
                            needsReconnect: false,
                          },
                        }
                      : p.services,
                  }))
                }
                ariaLabel={t('settings.linkedLoginVia').replace('{{service}}', t(SERVICE_META[id].nameKey))}
              />
            </li>
          ))}
        </ul>
      </section>

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.linkedSyncTitle')}</h2>
        <p className="settings-appear-block__desc">{t('settings.linkedSyncDesc')}</p>
        <ul className="settings-linked-toggles">
          <li className="settings-notif-row settings-appear-effect-row">
            <span className="settings-notif-row__icon" aria-hidden><FaUser /></span>
            <div className="settings-notif-row__text">
              <span className="settings-notif-row__title">{t('settings.linkedSyncAvatar')}</span>
              <span className="settings-notif-row__desc">{t('settings.linkedSyncAvatarDesc')}</span>
            </div>
            <ToggleSwitch
              checked={prefs.syncAvatar}
              onChange={(v) => setPrefs((p) => ({ ...p, syncAvatar: v }))}
              ariaLabel={t('settings.linkedSyncAvatar')}
            />
          </li>
          <li className="settings-notif-row settings-appear-effect-row">
            <span className="settings-notif-row__icon" aria-hidden><FaIdCard /></span>
            <div className="settings-notif-row__text">
              <span className="settings-notif-row__title">{t('settings.linkedSyncUsername')}</span>
              <span className="settings-notif-row__desc">{t('settings.linkedSyncUsernameDesc')}</span>
            </div>
            <ToggleSwitch
              checked={prefs.syncUsername}
              onChange={(v) => setPrefs((p) => ({ ...p, syncUsername: v }))}
              ariaLabel={t('settings.linkedSyncUsername')}
            />
          </li>
          <li className="settings-notif-row settings-appear-effect-row">
            <span className="settings-notif-row__icon" aria-hidden><FaBell /></span>
            <div className="settings-notif-row__text">
              <span className="settings-notif-row__title">{t('settings.linkedSyncNotifications')}</span>
              <span className="settings-notif-row__desc">{t('settings.linkedSyncNotificationsDesc')}</span>
            </div>
            <ToggleSwitch
              checked={prefs.syncNotifications}
              onChange={(v) => setPrefs((p) => ({ ...p, syncNotifications: v }))}
              ariaLabel={t('settings.linkedSyncNotifications')}
            />
          </li>
        </ul>
      </section>

      <div className="settings-linked-privacy">
        <FaShieldAlt className="settings-linked-privacy__icon" aria-hidden />
        <p>{t('settings.linkedPrivacyNote')}</p>
      </div>

      <div className="settings-notif-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          {t('settings.saveChanges')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('settings.cancel')}
        </button>
      </div>
    </div>
  );
}
